'use strict'

const { section, saving } = require('./sections')

function identifier (noun) {

  return [
    `${noun} title is the name in the app list. Unique title is the identifier in the address and in the designer. It must start with a letter or an underscore, then letters, digits, and underscores.`,
    'The unique title fills from the title until you edit it. Two apps on this account cannot share a unique title.'
  ]
}

const createApp = {
  title: 'Create an app',
  intro: 'An app is one project: its spreadsheets, pages, automations, and records. This dialog creates an empty app on the account you are signed in to.',
  sections: [
    section('Name', identifier('App').concat([
      'OK creates the app with no spreadsheets and no pages. Open it in the designer to build those. The app is not a template and it does not copy another app. Use copy or import template for that.'
    ])),
    saving('OK', 'the new app')
  ]
}

const editApp = {
  title: 'Edit an app',
  intro: 'This dialog changes the name and the description shown in the app list. It does not change the unique title, the spreadsheets, or the records.',
  sections: [
    section('Fields', [
      'App title is the name in the list and on the detail pane. Renaming it does not change the address. The unique title, which is the address, stays as it was when the app was created.',
      'Description is the paragraph on the detail pane. It is notes for you. It is not shown as a page inside the live app. Leave it blank when the title is enough.',
      'OK saves the title and the description. The statistics beside the detail pane are counted from the app. They are not edited here.'
    ]),
    saving('OK', 'the title and description')
  ]
}

const deleteApp = {
  title: 'Delete an app',
  intro: 'This dialog removes the app that is selected, including its spreadsheets, pages, and records.',
  sections: [
    section('Confirm', [
      'Type the app title exactly. The deletion is rejected until the text matches the title of the selected app. That check is there because the app cannot be restored from this dialog.',
      'Spreadsheets, records, pages, menus, automations, and schedules that belong to the app are removed with it. Other apps on the account are not touched.',
      'A template you saved from this app earlier is a separate copy. Deleting the app does not delete that template. Delete the template from the import dialog if it should go too.'
    ]),
    saving('OK', 'the removal of this app')
  ]
}

const copyApp = {
  title: 'Copy an app',
  intro: 'Copy creates a second app from the one that is selected. The original stays as it is.',
  sections: [
    section('Name', identifier('App').concat([
      'The unique title has to be one this account does not already use. The error under the field tells you when it is empty, when it has a character that is not allowed, or when another app already took it.'
    ])),
    section('Records', [
      'The structure is always copied: spreadsheets, columns, pages, views, menus, and the other definitions. Copy records also copies every row. Leave it unchecked for an empty copy of the structure.',
      'Relationship links between records are part of the copied rows. A copy without records keeps the relationship columns and has no links yet.',
      'Copy can take a while. The dialog switches to a progress bar and the buttons stay disabled until it finishes. Closing the dialog during the copy does not cancel the copy. An error is shown in the dialog and, if the dialog has closed, on the app list.'
    ]),
    saving('copy', 'the new app')
  ]
}

const saveTemplate = {
  title: 'Save as a template',
  intro: 'A template is a reusable copy of this app that can be imported into an account later. Saving a template does not change the app you saved it from.',
  sections: [
    section('Name and description', [
      'Template title is the name in the template list. Template id is the identifier. It must be letters, digits, and underscores. An id you already published is replaced by this save. The hint under the fields says that.',
      'Description is the sentence shown under the title when someone imports. Write what the template is for, not a change log.',
      'Include records stores the rows as well as the structure. Leave it off to publish only the spreadsheets, pages, and other definitions. The structure is always saved.',
      'Save publishes the template. The button stays disabled while the save runs. An error under the fields explains a rejected id or a failed save.'
    ]),
    saving('save', 'the template')
  ]
}

const importTemplate = {
  title: 'Import a template',
  intro: 'Import creates a new app from a template. It does not replace the app you currently have selected.',
  sections: [
    section('Choose a template', [
      'The list is the templates this account can see. Click one to select it. The description and the id are under the title. A template that was saved with records says so.',
      'An empty list means no template has been published yet. Save a template from an app first.',
      'While the list is loading, the dialog says so. Import stays disabled until a template is selected.'
    ]),
    section('Name the new app', [
      'App title and unique title name the app that will be created. The unique title has to be free on this account and has to be letters, digits, and underscores, starting with a letter or an underscore.',
      'Import records appears when the template contains records. Uncheck it to create the structure without the rows. When the template has no records, the checkbox is hidden and the new app has no rows.',
      'Delete this template removes the template itself. It is shown only for a template this account owns. It does not delete apps that were already imported from it. The button is separate from import.',
      'Import creates the app and selects it when the import finishes. The button stays disabled while the import runs.'
    ]),
    saving('import', 'a new app from the template')
  ]
}

module.exports = {
  'create-app': createApp,
  'edit-app': editApp,
  'delete-app': deleteApp,
  'copy-app': copyApp,
  'save-template': saveTemplate,
  'import-template': importTemplate
}
